import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Paper,
  Grid,
  IconButton,
  Fade,
  Grow,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Chat as ChatIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationOnIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  Favorite as FavoriteIcon,
  Assessment as AssessmentIcon,
  Subscriptions as SubscriptionsIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import { theme, responsiveSpacing, responsiveFontSizes } from '../../theme/theme';
import { useNavigate } from 'react-router-dom';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
}));

const GradientBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  color: 'white',
}));

const ActionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
}));

const ActionIcon = styled(Box)<{ color: string }>(({ color, theme }) => ({
  width: 56,
  height: 56,
  borderRadius: 28,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: `${color}20`,
  margin: '0 auto',
  marginBottom: theme.spacing(1),
}));

const WisdomCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  overflow: 'auto',
  background: `linear-gradient(135deg, ${theme.palette.secondary.main}40, ${theme.palette.primary.main}40)`,
  marginBottom: theme.spacing(2),
}));

const HomeScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { birthInfo } = useUser();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '早上好';
    if (hour < 18) return '下午好';
    return '晚上好';
  };

  const getZodiacEmoji = () => {
    // Simplified zodiac calculation
    const month = new Date().getMonth() + 1;
    const zodiacs = ['🐭', '🐮', '🐯', '🐰', '🐲', '🐍', '🐴', '🐑', '🐵', '🐔', '🐶', '🐷'];
    return zodiacs[month % 12];
  };

  const getPersonalityInsight = () => {
    return '今日星象显示，您的运势正在上升，适合做出重要决定。';
  };

  const quickActions = [
    {
      id: 'astrolabe',
      title: '命盘',
      subtitle: '查看您的紫微斗数命盘',
      icon: CalendarIcon,
      color: theme.colors.accent,
      onPress: () => navigate('/astrolabe'),
      disabled: !birthInfo,
    },
    {
      id: 'consultation',
      title: 'AI咨询',
      subtitle: '向星象询问',
      icon: ChatIcon,
      color: theme.colors.primary,
      onPress: () => navigate('/consultation'),
      disabled: !birthInfo,
    },
    {
      id: 'subscription',
      title: '订阅',
      subtitle: '管理订阅',
      icon: SubscriptionsIcon,
      color: theme.colors.accent,
      onPress: () => navigate('/subscription-management'),
      disabled: false,
    },
    {
      id: 'soulmate-painting',
      title: '灵魂伴侣画像',
      subtitle: '真爱画像',
      icon: FavoriteIcon,
      color: '#E91E63',
      onPress: () => navigate('/soulmate-painting'),
      disabled: !birthInfo,
    },
    {
      id: 'reports',
      title: '报告',
      subtitle: '运势报告',
      icon: AssessmentIcon,
      color: '#9C27B0',
      onPress: () => navigate('/reports'),
      disabled: !birthInfo,
    },
    {
      id: 'history',
      title: '历史',
      subtitle: '过往解读',
      icon: ChatIcon,
      color: '#FF6B6B',
      onPress: () => navigate('/consultation/history'),
      disabled: !birthInfo,
    },
    {
      id: 'profile',
      title: '个人资料',
      subtitle: '您的设置',
      icon: LocationOnIcon,
      color: '#4ECDC4',
      onPress: () => navigate('/profile'),
      disabled: false,
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: theme.colors.background,
      paddingBottom: { xs: '80px', sm: '80px' } // 为固定底部导航留出空间
    }}>
      {/* Header Section */}
      <GradientBox>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: { xs: responsiveSpacing.xs, sm: 0 }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            flex: { xs: '1 1 100%', sm: '1 1 auto' },
            minWidth: 0
          }}>
            <Avatar
              sx={{
                width: { xs: 40, sm: 50 },
                height: { xs: 40, sm: 50 },
                backgroundColor: theme.colors.accent,
                marginRight: { xs: responsiveSpacing.xs, sm: responsiveSpacing.component },
                flexShrink: 0
              }}
            >
              {user?.displayName?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#F5F5DC', 
                  opacity: 0.9,
                  fontSize: responsiveFontSizes.body2
                }}
              >
                {getGreeting()},
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#FFFFFF',
                  fontSize: responsiveFontSizes.h4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {user?.displayName || 'User'}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            sx={{ 
              color: 'white',
              flexShrink: 0,
              padding: { xs: responsiveSpacing.xs, sm: responsiveSpacing.xs }
            }}
          >
            <NotificationsIcon sx={{ fontSize: responsiveFontSizes.h4 }} />
          </IconButton>
        </Box>
      </GradientBox>

      <Box sx={{ padding: responsiveSpacing.component, marginTop: `-${responsiveSpacing.component}` }}>


        {/* Quick Actions */}
        <Grow in timeout={800} style={{ transitionDelay: '200ms' }}>
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 'bold', 
                color: theme.colors.primary, 
                mb: responsiveSpacing.xs, 
                mt: responsiveSpacing.xs,
                fontSize: responsiveFontSizes.h4
              }}
            >
              快捷操作
            </Typography>
            <Grid 
              container 
              spacing={2} 
              sx={{ mb: responsiveSpacing.component }}
            >
              {quickActions.map((action) => {
                const IconComponent = action.icon;
                return (
                  <Grid item xs={6} sm={6} md={3} key={action.id}>
                    <ActionCard
                      onClick={action.disabled ? undefined : action.onPress}
                      sx={{
                        height: { xs: '100px', sm: '120px' },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: { xs: responsiveSpacing.xs, sm: responsiveSpacing.component },
                        minHeight: { xs: '90px', sm: '100px' },
                        cursor: action.disabled ? 'not-allowed' : 'pointer',
                        opacity: action.disabled ? 0.5 : 1,
                        '&:hover': {
                          transform: action.disabled ? 'none' : 'translateY(-2px)',
                          boxShadow: action.disabled ? 'none' : '0 8px 24px rgba(0,0,0,0.15)',
                        }
                      }}
                    >
                      <ActionIcon 
                        color={action.color}
                        sx={{
                          width: { xs: 40, sm: 56 },
                          height: { xs: 40, sm: 56 },
                          borderRadius: { xs: 20, sm: 28 },
                          marginBottom: { xs: responsiveSpacing.xs, sm: responsiveSpacing.xs }
                        }}
                      >
                        <IconComponent
                          sx={{
                            fontSize: { xs: 20, sm: 28 },
                            color: action.disabled ? theme.colors.placeholder : action.color,
                          }}
                        />
                      </ActionIcon>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 'bold',
                          color: action.disabled ? theme.colors.placeholder : theme.colors.text,
                          mb: responsiveSpacing.xs,
                          fontSize: responsiveFontSizes.body2,
                          textAlign: 'center',
                          lineHeight: 1.2
                        }}
                      >
                        {action.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.colors.placeholder,
                          fontSize: responsiveFontSizes.body2,
                          textAlign: 'center',
                          lineHeight: 1.1
                        }}
                      >
                        {action.subtitle}
                      </Typography>
                    </ActionCard>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Grow>

        {/* Features Link */}
        <Grow in timeout={800} style={{ transitionDelay: '400ms' }}>
          <StyledCard>
            <CardContent sx={{ p: { xs: responsiveSpacing.xs, sm: responsiveSpacing.component } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: { xs: responsiveSpacing.component, sm: responsiveSpacing.component }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveSpacing.xs }}>
                  <AssessmentIcon 
                    sx={{ 
                      color: theme.colors.primary, 
                      fontSize: responsiveFontSizes.h4,
                      flexShrink: 0
                    }} 
                  />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: theme.colors.primary,
                      fontSize: responsiveFontSizes.h4
                    }}
                  >
                    探索功能
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: theme.colors.text,
                  lineHeight: { xs: 1.5, sm: 1.6 },
                  mb: { xs: responsiveSpacing.section, sm: responsiveSpacing.container },
                  fontSize: responsiveFontSizes.body1
                }}
              >
                此处了解功能详情
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/features')}
                sx={{
                  borderRadius: 2,
                  backgroundColor: theme.colors.primary,
                  '&:hover': {
                    backgroundColor: theme.colors.accent,
                  },
                  fontWeight: 'bold',
                  fontSize: responsiveFontSizes.body2
                }}
              >
                了解更多
              </Button>
            </CardContent>
          </StyledCard>
        </Grow>

        {/* Daily Wisdom */}
        <Grow in timeout={800} style={{ transitionDelay: '500ms' }}>
          <WisdomCard>
            <CardContent sx={{ p: { xs: responsiveSpacing.xs, sm: responsiveSpacing.component } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: { xs: responsiveSpacing.xs, sm: responsiveSpacing.component },
                gap: responsiveSpacing.xs
              }}>
                <LightbulbOutlinedIcon 
                  sx={{ 
                    color: theme.colors.primary, 
                    fontSize: responsiveFontSizes.h4,
                    flexShrink: 0
                  }} 
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: theme.colors.primary,
                    fontSize: responsiveFontSizes.h4
                  }}
                >
                  今日智慧
                </Typography>
                <Chip 
                  label={getZodiacEmoji()} 
                  size="small" 
                  sx={{ 
                    ml: 'auto',
                    fontSize: responsiveFontSizes.body2
                  }} 
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: theme.colors.text,
                  fontStyle: 'italic',
                  lineHeight: { xs: 1.5, sm: 1.6 },
                  mb: { xs: responsiveSpacing.xs, sm: responsiveSpacing.xs },
                  fontSize: responsiveFontSizes.body1
                }}
              >
                {getPersonalityInsight()}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.colors.placeholder, 
                  textAlign: 'right',
                  fontSize: responsiveFontSizes.body2
                }}
              >
                古语有云
              </Typography>
            </CardContent>
          </WisdomCard>
        </Grow>
      </Box>
    </Box>
  );
};

export default HomeScreen;